import { z } from "zod";
import { type FeatureFlags } from "./clientConfig.js";

// --- Typography & Theme Tokens ---

export const FontIdSchema = z.enum([
  "inter",
  "playfair-display",
  "lato",
  "merriweather",
  "montserrat",
  "raleway",
  "source-sans",
  "libre-baskerville",
  "oswald",
  "nunito",
  "zilla-slab",
  "eb-garamond",
  "cormorant-garamond",
  "dm-serif-display",
  "crimson-text",
]);
export type FontId = z.infer<typeof FontIdSchema>;

export const TypographyScaleSchema = z.enum(["compact", "default", "spacious"]);

export const ThemeTokensSchema = z.object({
  // OKLCH color strings — overridden at runtime via CSS setProperty
  colors: z
    .object({
      primary: z.string().optional(),
      primaryForeground: z.string().optional(),
      secondary: z.string().optional(),
      secondaryForeground: z.string().optional(),
      accent: z.string().optional(),
      accentForeground: z.string().optional(),
      background: z.string().optional(),
      foreground: z.string().optional(),
      muted: z.string().optional(),
      mutedForeground: z.string().optional(),
      destructive: z.string().optional(),
      border: z.string().optional(),
    })
    .default({}),
  typography: z
    .object({
      headingFont: FontIdSchema.default("inter"),
      bodyFont: FontIdSchema.default("inter"),
      scale: TypographyScaleSchema.default("default"),
    })
    .default({}),
  radius: z.string().default("0.5rem"),
  shadow: z.enum(["none", "soft", "sharp"]).default("soft"),
  spacing: z.enum(["tight", "default", "airy"]).default("default"),
});

export type ThemeTokens = z.infer<typeof ThemeTokensSchema>;

// --- Section animation ---

export const SectionAnimationPresetSchema = z.enum([
  "none",
  "fade-up",
  "fade-in",
  "slide-left",
  "slide-right",
  "stagger-children",
  "reveal-clip",
  "parallax-bg",
  "sticky-exit",
  // Storytelling presets
  "scrub-parallax",   // continuous parallax tied to scroll position
  "zoom-out",         // section enters scaled-up, resolves to 1 on scroll
  "stagger-cascade",  // dramatic stagger with y + scale start
  "word-reveal",      // per-word clip reveal on headline
]);
export type SectionAnimationPreset = z.infer<typeof SectionAnimationPresetSchema>;

export const SectionAnimationSchema = z
  .object({
    /** Named animation preset applied when this section scrolls into view. */
    preset: SectionAnimationPresetSchema,
    /** Animation duration in seconds (default: 0.85). */
    duration: z.number().positive(),
    /** Delay before the animation starts, in seconds (default: 0). */
    delay: z.number(),
    /** GSAP ease string (default: "power3.out"). */
    ease: z.string(),
    /** When true (or a number), scrubs with scroll position instead of triggering once (default: false). */
    scrub: z.union([z.boolean(), z.number()]),
    /** ScrollTrigger start value (default: "top 88%"). */
    start: z.string(),
    /** Seconds between children for stagger-children preset (default: 0.1). */
    stagger: z.number(),
    /** Pixel distance for translate-based presets — fade-up, slide-* (default: 48). */
    distance: z.number(),
  })
  .partial(); // All fields optional; defaults live in the animation engine
export type SectionAnimation = z.infer<typeof SectionAnimationSchema>;

// --- Section definitions ---

export const SectionTypeSchema = z.enum([
  // Food & bev
  "hero",
  "menu",
  "reservations",
  "gallery",
  "story",
  "map",
  "hours",
  "cta",
  "order",
  "social",
  "testimonials",
  "team",
  "private-events",
  "pricing",
  "custom",
  // Service vertical
  "services",     // fixed-price packages or service catalog
  "quote",        // config-driven quote / contact form (FormRunner)
  "service-area", // coverage map with radius circle
]);
export type SectionType = z.infer<typeof SectionTypeSchema>;

export const SectionSchema = z.object({
  id: z.string(),
  type: SectionTypeSchema,
  variant: z.string(),
  props: z.record(z.string(), z.unknown()).default({}),
  visibleWhen: z
    .object({
      feature: z.string() as z.ZodType<keyof FeatureFlags>,
    })
    .optional(),
  /** Optional scroll animation applied to this section. Omit for no animation. */
  animation: SectionAnimationSchema.optional(),
});
export type Section = z.infer<typeof SectionSchema>;

// --- Multi-page routing ---

/**
 * An optional additional page/route beyond the home page.
 * Each route has its own section composition rendered by the same SectionRenderer.
 * Routing is opt-in: if routes[] is empty the app stays single-page.
 */
export const RouteConfigSchema = z.object({
  /** URL path, e.g. "/menu", "/gallery". Must start with "/". */
  path: z.string().regex(/^\//),
  /** Human-readable label for nav links, e.g. "Menu" */
  label: z.string(),
  /** Optional lucide icon name for nav items, e.g. "utensils" */
  icon: z.string().optional(),
  sections: z.array(SectionSchema).default([]),
});
export type RouteConfig = z.infer<typeof RouteConfigSchema>;

/** Controls the auto-generated site nav. Only shown when routes[] is non-empty. */
export const NavConfigSchema = z.object({
  show: z.boolean().default(true),
  position: z.enum(["top", "bottom"]).default("top"),
  /** "minimal" = logo + links; "pill" = floating pill; "full" = full-width bar */
  style: z.enum(["minimal", "pill", "full"]).default("minimal"),
  /** Override the home link label (default: business name) */
  homeLabel: z.string().optional(),
  /** Optional logo image URL */
  logoUrl: z.string().url().optional(),
  /** Primary CTA button label shown in the nav (e.g. "Reserve a Table") */
  ctaLabel: z.string().optional(),
  /** CTA button href — anchor (#reservations) or route path (/book) */
  ctaTarget: z.string().optional(),
});
export type NavConfig = z.infer<typeof NavConfigSchema>;

// --- Footer ---

export const SocialPlatformSchema = z.enum([
  "instagram",
  "facebook",
  "tiktok",
  "twitter",
  "youtube",
  "yelp",
  "google",
  "linkedin",
  "pinterest",
]);
export type SocialPlatform = z.infer<typeof SocialPlatformSchema>;

export const SiteFooterSchema = z.object({
  show: z.boolean().default(true),
  /** Short brand tagline shown below the business name */
  tagline: z.string().optional(),
  /** Social platform links */
  socials: z
    .array(z.object({ platform: SocialPlatformSchema, url: z.string() }))
    .default([]),
  /** Extra footer links (e.g. Privacy, Contact) */
  links: z
    .array(z.object({ label: z.string(), href: z.string() }))
    .default([]),
  /** Legal entity name for the copyright line (defaults to businessName) */
  legalName: z.string().optional(),
});
export type SiteFooter = z.infer<typeof SiteFooterSchema>;

// --- Full SiteConfig ---

export const SiteConfigSchema = z.object({
  version: z.number().int().default(1),
  clientId: z.string(),
  theme: ThemeTokensSchema.default({}),
  fonts: z.array(FontIdSchema).default(["inter"]),
  /** Dark-mode color overrides. Keys mirror theme.colors. Present when themeMode is "dark" or "both". */
  darkColors: z.record(z.string()).optional(),
  /** Whether the site runs light-only, dark-only, or supports a runtime toggle. */
  themeMode: z.enum(["light", "dark", "both"]).default("light"),

  /** Home page sections (rendered at "/") */
  sections: z.array(SectionSchema).default([]),

  /**
   * Optional additional routes. Each gets its own URL and section composition.
   * Empty array = single-page mode (no router mounted).
   */
  routes: z.array(RouteConfigSchema).default([]),

  /**
   * Nav config. Only relevant when routes[] is non-empty.
   * Defaults to shown when routes exist.
   */
  nav: NavConfigSchema.optional(),

  /** Footer config. Auto-rendered on every page below sections. */
  footer: SiteFooterSchema.optional(),

  customCssUrl: z.string().url().optional(),
  updatedAt: z.string().optional(),
});

export type SiteConfig = z.infer<typeof SiteConfigSchema>;

// --- Starters ---

/**
 * Home page sections — one per animation preset so the demo showcases all effects.
 *
 * Order / animation mapping:
 *   hero-split          → entrance (load-time word-reveal, no ScrollTrigger)
 *   story-split         → slide-left
 *   gallery-grid        → stagger-children  (each cell uses data-stagger-child)
 *   reservations-inline → fade-up
 *   parallax-break      → parallax-bg
 *   hours-simple        → stagger-children  (each row uses data-stagger-child)
 *   cta-banner          → reveal-clip
 *   map-embed           → fade-in
 */
export const FOOD_BEV_HOME_SECTIONS: Section[] = [
  {
    id: "hero",
    type: "hero",
    variant: "hero-split",
    props: {
      headline: "Where Every Meal Becomes a Memory",
      subheadline: "Seasonal ingredients, crafted by hand, served with intention.",
      ctaLabel: "View Menu",
      ctaTarget: "/menu",
    },
    animation: { preset: "none" },
  },
  {
    id: "story",
    type: "story",
    variant: "story-split",
    props: {
      eyebrow: "Our Story",
      headline: "Rooted in the neighborhood, driven by craft",
      body: "We opened our doors with one belief — that exceptional food shouldn't require a special occasion. Every dish is prepared with locally sourced ingredients and a commitment to the craft that's been with us from day one.",
      stat: "12+",
      statLabel: "Years serving the neighborhood",
    },
    animation: { preset: "slide-left" },
  },
  {
    id: "gallery",
    type: "gallery",
    variant: "gallery-grid",
    props: {
      headline: "A Taste of What We Do",
    },
    animation: { preset: "stagger-children", stagger: 0.07 },
  },
  {
    id: "reservations",
    type: "reservations",
    variant: "reservations-inline",
    props: {},
    animation: { preset: "fade-up" },
  },
  {
    id: "parallax-break",
    type: "custom",
    variant: "parallax-break",
    props: {
      quote: "Food is our common ground, a universal experience.",
      attribution: "James Beard",
    },
    animation: { preset: "parallax-bg" },
  },
  {
    id: "hours",
    type: "hours",
    variant: "hours-simple",
    props: {},
    animation: { preset: "stagger-children", stagger: 0.07 },
  },
  {
    id: "cta",
    type: "cta",
    variant: "cta-banner",
    props: {
      headline: "An evening to remember",
      subheadline: "Reserve your table and let us take care of the rest.",
      ctaLabel: "Reserve a Table",
      secondaryLabel: "View Menu",
    },
    animation: { preset: "reveal-clip" },
  },
  {
    id: "map",
    type: "map",
    variant: "map-embed",
    props: {
      headline: "Find Us",
      address: "1 Market Street, San Francisco, CA 94105",
      phone: "(415) 555-0100",
      email: "hello@example.com",
    },
    animation: { preset: "fade-in" },
  },
];

/** Dedicated /menu route sections. */
export const FOOD_BEV_MENU_SECTIONS: Section[] = [
  {
    id: "menu-full",
    type: "menu",
    variant: "menu-magazine",
    props: { categoryNav: "sidebar", fullPage: true },
  },
];

/** Starter routes for a new food-bev client. */
export const FOOD_BEV_STARTER_ROUTES: RouteConfig[] = [
  {
    path: "/menu",
    label: "Menu",
    icon: "utensils",
    sections: FOOD_BEV_MENU_SECTIONS,
  },
];

/**
 * @deprecated Use FOOD_BEV_HOME_SECTIONS + FOOD_BEV_STARTER_ROUTES instead.
 * Kept for any Phase 1 seeds already in Firestore.
 */
export const FOOD_BEV_STARTER_SECTIONS: Section[] = FOOD_BEV_HOME_SECTIONS;
